// askAdminForSre — main-app → sfa-admin SRE error-ingest.
//
// Sibling of askAdminForHelp, but for runtime exceptions that should
// land as engineering incidents instead of subscriber-facing helps.
// Sends to a different endpoint on the same admin panel; reuses the
// same AGENT_TOOL_SECRET shared bearer.
//
// Contract from the sfa-admin assistant (2026-07-11):
//   POST https://sfa-admin.vercel.app/api/ingest/main-error
//   Header: x-agent-secret: <AGENT_TOOL_SECRET>
//   Body:   { message, stack, route, level, source: 'main-app', meta: { owner_id } }
//   Fire-and-forget. Dedup happens server-side (hash of message+route,
//   6h window). Response: 201 new / 200 deduped / 401 on secret drift.
//   We don't act on the response.
//
// Skips these noise classes so the sfa-admin dashboard doesn't drown
// in them: user-cancelled fetches, transient network hiccups, rate
// limits, and 401/403s (auth churn, not code bugs).

const TIMEOUT_MS = 800;

// UUIDv4 (best-effort). Prefer crypto.randomUUID; fall back to a
// deterministic-length pseudo-uuid so we always have a correlation
// id even on stone-age browsers without SubtleCrypto.
function makeRequestId() {
  try {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  } catch { /* fall through */ }
  const hex = '0123456789abcdef';
  let out = '';
  for (let i = 0; i < 36; i++) {
    if (i === 8 || i === 13 || i === 18 || i === 23) out += '-';
    else if (i === 14) out += '4';
    else out += hex[Math.floor(Math.random() * 16)];
  }
  return out;
}
const IGNORED_MESSAGE_RE = /aborted|ECONNRESET|ETIMEDOUT|rate.?limit|\b40[13]\b|auth/i;

// Coarse secret-scrubber. Runs on the STACK before we send it. Belt
// and suspenders on top of whatever the caller passed us — the admin
// side re-scrubs but we don't rely on it.
const SECRET_PATTERNS = [
  /Bearer\s+[A-Za-z0-9._-]{16,}/gi,
  /sk-[a-z]+-[A-Za-z0-9_-]{20,}/gi,
  /sk-[A-Za-z0-9_-]{20,}/gi,
  /whsec_[A-Za-z0-9]{20,}/gi,
  /eyJ[A-Za-z0-9._-]{20,}/g,        // JWTs
  /re_[A-Za-z0-9_-]{20,}/gi,        // Resend
  /pk_(?:live|test)_[A-Za-z0-9]{16,}/gi,
  /sk_(?:live|test)_[A-Za-z0-9]{16,}/gi,
];
function scrubSecrets(s) {
  let out = String(s || '');
  for (const re of SECRET_PATTERNS) out = out.replace(re, '[redacted]');
  return out;
}

/**
 * Fire-and-forget SRE signal to the sfa-admin ingest endpoint.
 *
 * @param {Object} args
 * @param {Error|string} args.error   the thrown error or its message
 * @param {string} [args.route]        the API route or UI path that
 *                                     was executing when the error hit
 *                                     (e.g. '/api/twilio/provision',
 *                                     '/leads' — DO NOT include query
 *                                     strings that carry PII)
 * @param {string} [args.level]        'error' | 'warn' | 'info'
 *                                     (default 'error')
 * @param {string} [args.owner_id]     Supabase user_id if known
 * @returns {void}   Never throws. Never blocks.
 */
export function askAdminForSre({ error, route, level, owner_id } = {}) {
  try {
    const message = String((error && error.message) || error || '').slice(0, 500);
    if (!message) return;

    // Server-side of the admin dedups on (message + route); we prune
    // the loudest noise on the client so it never gets counted at all.
    if (IGNORED_MESSAGE_RE.test(message)) return;

    const rawStack = (error && error.stack) ? String(error.stack) : '';
    const stack    = scrubSecrets(rawStack).slice(0, 4000);
    const cleanMsg = scrubSecrets(message);

    const request_id = makeRequestId();
    const body = JSON.stringify({
      request_id,
      message: cleanMsg,
      stack,
      route:   route ? String(route).slice(0, 200) : null,
      level:   ['error', 'warn', 'info'].includes(level) ? level : 'error',
      source:  'main-app',
      meta:    { owner_id: owner_id || null },
    });

    const controller = new AbortController();
    const t = setTimeout(() => controller.abort(), TIMEOUT_MS);

    // The server-side proxy at /api/subscriber-help/signal attaches
    // the secret from AGENT_TOOL_SECRET. We use the same pattern here
    // so the browser never sees the bearer. See /api/sre/signal.js.
    fetch('/api/sre/signal', {
      method:    'POST',
      headers:   { 'Content-Type': 'application/json' },
      body,
      signal:    controller.signal,
      keepalive: true,
    })
      .then(() => {}, () => {})
      .finally(() => clearTimeout(t));
  } catch { /* never surface — fire-and-forget contract */ }
}

// Also exported as a default for import convenience.
export default askAdminForSre;
